import * as React from 'react';
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  Filter,
  User,
  MapPin,
  Briefcase,
  Building2,
  CheckCircle2,
  Circle,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TablePagination, DEFAULT_PAGE_SIZE, paginate } from '@/components/TablePagination';
import {
  TABLE_SCROLL_CONTAINER_CLASS,
  TABLE_HEADER_ROW_CLASS,
  TABLE_HEAD_CLASS,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
  TABLE_STICKY_HEADER_STYLE,
} from '@/lib/table-styles';
import { Resource, ResourceSkill, ResourceType, UtilisationTier } from '@/types';
import {
  DEFAULT_UTILIZATION_TIERS,
  getUtilizationColor as getTierColor,
  getUtilizationLabel as getTierLabel,
} from '@/utils/utilization-labels';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortColumn = 'name' | 'role' | 'department' | 'location' | 'type' | 'utilization';

// ─── Skills helpers ───────────────────────────────────────────────────────────

const MAX_SKILL_TAGS = 3;

/** Rating (1–5) → filled dot count for visual indicator */
function SkillTag({ skill }: { skill: ResourceSkill }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-medium text-foreground leading-none">
      {skill.name}
      <span className="flex items-center gap-px ml-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-1 w-1 rounded-full',
              i < skill.rating ? 'bg-primary' : 'bg-muted-foreground/25'
            )}
          />
        ))}
      </span>
    </span>
  );
}
type SortDirection = 'asc' | 'desc' | null;

interface ResourceWithUtilization extends Resource {
  utilization?: number;
}

interface ResourceSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resources: Resource[];
  selectedResourceIds: string[];
  onConfirm: (resourceIds: string[]) => void;
  calculateUtilization?: (resourceId: string) => number;
  utilisationTiers?: UtilisationTier[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 250;

const RESOURCE_TYPES: { label: string; value: ResourceType }[] = [
  { label: 'Permanent', value: 'permanent' },
  { label: 'Contractor', value: 'contractor' },
  { label: 'Agency', value: 'agency' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return String(name ?? '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Darken a hex colour so pastel tier colours stay readable as text on a light
// background. Mirrors HeatmapLegend's darkenHex.
function darkenHex(hex: string, amount = 70): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = Math.max(0, parseInt(clean.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(clean.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(clean.slice(4, 6), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function getTypeLabel(type: ResourceType): string {
  switch (type) {
    case 'permanent':
      return 'Permanent';
    case 'contractor':
      return 'Contractor';
    case 'agency':
      return 'Agency';
    default:
      return type;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ResourceSearchModal({
  open,
  onOpenChange,
  resources,
  selectedResourceIds,
  onConfirm,
  calculateUtilization,
  utilisationTiers,
}: ResourceSearchModalProps) {
  // ── Utilisation bands (colour + label) from admin settings ────────────────
  const tiers = React.useMemo(
    () =>
      utilisationTiers && utilisationTiers.length > 0
        ? utilisationTiers
        : DEFAULT_UTILIZATION_TIERS,
    [utilisationTiers]
  );

  // ── Search & sort state ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);

  // ── Local selection (committed on confirm) ────────────────────────────────
  const [pendingIds, setPendingIds] = React.useState<string[]>([]);

  // ── Pending filter state (applied on "Apply Filters") ────────────────────
  const [pendingRoleFilter, setPendingRoleFilter] = React.useState<string[]>([]);
  const [pendingDepartmentFilter, setPendingDepartmentFilter] = React.useState<string[]>([]);
  const [pendingLocationFilter, setPendingLocationFilter] = React.useState<string[]>([]);
  const [pendingTypeFilter, setPendingTypeFilter] = React.useState<string[]>([]);
  const [pendingSkillFilter, setPendingSkillFilter] = React.useState<string[]>([]);
  const [pendingAvailableOnly, setPendingAvailableOnly] = React.useState(false);

  // ── Applied filter state ──────────────────────────────────────────────────
  const [roleFilter, setRoleFilter] = React.useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = React.useState<string[]>([]);
  const [locationFilter, setLocationFilter] = React.useState<string[]>([]);
  const [typeFilter, setTypeFilter] = React.useState<string[]>([]);
  const [skillFilter, setSkillFilter] = React.useState<string[]>([]);
  const [availableOnly, setAvailableOnly] = React.useState(false);

  const activeFilterCount =
    roleFilter.length +
    departmentFilter.length +
    locationFilter.length +
    typeFilter.length +
    skillFilter.length +
    (availableOnly ? 1 : 0);

  // ── Search debounce ───────────────────────────────────────────────────────
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Reset on open ─────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (open) {
      setPendingIds([...selectedResourceIds]);
      setSearchQuery('');
      setDebouncedSearch('');
      setSortColumn('name');
      setSortDirection('asc');
      setShowFilters(false);
      setPage(1);
      setPageSize(DEFAULT_PAGE_SIZE);
      resetPendingFilters();
      resetAppliedFilters();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetPendingFilters() {
    setPendingRoleFilter([]);
    setPendingDepartmentFilter([]);
    setPendingLocationFilter([]);
    setPendingTypeFilter([]);
    setPendingSkillFilter([]);
    setPendingAvailableOnly(false);
  }

  function resetAppliedFilters() {
    setRoleFilter([]);
    setDepartmentFilter([]);
    setLocationFilter([]);
    setTypeFilter([]);
    setSkillFilter([]);
    setAvailableOnly(false);
  }

  // ── Enrich resources with utilization ────────────────────────────────────
  const enriched: ResourceWithUtilization[] = React.useMemo(
    () =>
      resources.map((r) => ({
        ...r,
        utilization: calculateUtilization ? calculateUtilization(r.id) : undefined,
      })),
    [resources, calculateUtilization]
  );

  // ── Unique filter options ─────────────────────────────────────────────────
  const uniqueRoles = React.useMemo(
    () => [...new Set(resources.map((r) => r.role).filter(Boolean))].sort(),
    [resources]
  );
  const uniqueDepartments = React.useMemo(
    () =>
      [...new Set(resources.map((r) => r.department).filter((d): d is string => Boolean(d)))].sort(),
    [resources]
  );
  const uniqueLocations = React.useMemo(
    () =>
      [...new Set(resources.map((r) => r.location).filter((l): l is string => Boolean(l)))].sort(),
    [resources]
  );
  const uniqueSkills = React.useMemo(
    () =>
      [...new Set(
        resources.flatMap((r) => (r.skills ?? []).map((s) => s.name))
      )].sort(),
    [resources]
  );

  // ── Fuzzy search ──────────────────────────────────────────────────────────
  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    if (!text) return false;
    const t = text.toLowerCase();
    const q = query.toLowerCase();
    if (t.includes(q)) return true;
    let qi = 0;
    for (let i = 0; i < t.length && qi < q.length; i++) {
      if (t[i] === q[qi]) qi++;
    }
    return qi === q.length;
  }, []);

  // ── Filter + search ───────────────────────────────────────────────────────
  const filtered = React.useMemo(() => {
    let list = enriched;

    if (availableOnly) {
      list = list.filter((r) => (r.utilization ?? 0) < 70);
    }
    if (roleFilter.length > 0) {
      list = list.filter((r) => roleFilter.includes(r.role));
    }
    if (departmentFilter.length > 0) {
      list = list.filter((r) => r.department && departmentFilter.includes(r.department));
    }
    if (locationFilter.length > 0) {
      list = list.filter((r) => r.location && locationFilter.includes(r.location));
    }
    if (typeFilter.length > 0) {
      list = list.filter((r) => typeFilter.includes(r.type));
    }
    if (skillFilter.length > 0) {
      list = list.filter((r) =>
        skillFilter.every((sk) =>
          (r.skills ?? []).some((s) => s.name === sk)
        )
      );
    }
    if (debouncedSearch) {
      list = list.filter(
        (r) =>
          fuzzyMatch(r.name, debouncedSearch) ||
          fuzzyMatch(r.role, debouncedSearch) ||
          fuzzyMatch(r.department ?? '', debouncedSearch) ||
          fuzzyMatch(r.location ?? '', debouncedSearch) ||
          (r.skills ?? []).some((s) => fuzzyMatch(s.name, debouncedSearch))
      );
    }

    return list;
  }, [enriched, debouncedSearch, availableOnly, roleFilter, departmentFilter, locationFilter, typeFilter, fuzzyMatch]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sorted = React.useMemo(() => {
    if (!sortDirection) return filtered;

    return [...filtered].sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';

      switch (sortColumn) {
        case 'name':
          av = a.name.toLowerCase();
          bv = b.name.toLowerCase();
          break;
        case 'role':
          av = a.role.toLowerCase();
          bv = b.role.toLowerCase();
          break;
        case 'department':
          av = (a.department ?? '').toLowerCase();
          bv = (b.department ?? '').toLowerCase();
          break;
        case 'location':
          av = (a.location ?? '').toLowerCase();
          bv = (b.location ?? '').toLowerCase();
          break;
        case 'type':
          av = a.type.toLowerCase();
          bv = b.type.toLowerCase();
          break;
        case 'utilization':
          av = a.utilization ?? -1;
          bv = b.utilization ?? -1;
          break;
      }

      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortColumn, sortDirection]);

  const displayed = React.useMemo(
    () => paginate(sorted, page, pageSize),
    [sorted, page, pageSize]
  );

  // Searching and filtering can shrink the result set below the current page
  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [sorted.length, page, pageSize]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortDirection('asc');
        setSortColumn('name');
      } else setSortDirection('asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const handleToggle = (id: string) => {
    setPendingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const ids = sorted.map((r) => r.id);
    setPendingIds((prev) => [...new Set([...prev, ...ids])]);
  };

  const handleDeselectAll = () => {
    const ids = new Set(sorted.map((r) => r.id));
    setPendingIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const handleApplyFilters = () => {
    setRoleFilter(pendingRoleFilter);
    setDepartmentFilter(pendingDepartmentFilter);
    setLocationFilter(pendingLocationFilter);
    setTypeFilter(pendingTypeFilter);
    setSkillFilter(pendingSkillFilter);
    setAvailableOnly(pendingAvailableOnly);
    setPage(1);
  };

  const handleClearFilters = () => {
    resetPendingFilters();
    resetAppliedFilters();
    setPage(1);
  };

  const handleConfirm = () => {
    onConfirm(pendingIds);
    onOpenChange(false);
  };

  // ── Sort icon ─────────────────────────────────────────────────────────────
  const renderSortIcon = (col: SortColumn) => {
    if (sortColumn !== col || !sortDirection) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="ml-1 h-3.5 w-3.5" />
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        {/* ── Dialog header ──────────────────────────────────────────────── */}
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-semibold">Select Resources</DialogTitle>
            {pendingIds.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {pendingIds.length} selected
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* ── Main content ─────────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Search bar + meta row */}
            <div className="px-4 py-3 border-b shrink-0 space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search by name, role, department or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9 h-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        'h-9 w-9 shrink-0 relative',
                        showFilters && 'bg-muted border-primary/40'
                      )}
                      onClick={() => setShowFilters((v) => !v)}
                    >
                      <Filter className="h-4 w-4" />
                      {activeFilterCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Filters</TooltipContent>
                </Tooltip>
              </div>

              {/* Meta row: count + bulk actions */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{sorted.length}</span>{' '}
                  resource{sorted.length !== 1 ? 's' : ''} found
                  {pendingIds.length > 0 && (
                    <>
                      {' '}
                      &bull;{' '}
                      <span className="font-medium text-foreground">{pendingIds.length}</span>{' '}
                      selected
                    </>
                  )}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2 text-muted-foreground hover:text-foreground"
                    onClick={handleSelectAll}
                    disabled={sorted.length === 0}
                  >
                    Select all
                  </Button>
                  <span className="text-muted-foreground text-xs">·</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2 text-muted-foreground hover:text-foreground"
                    onClick={handleDeselectAll}
                    disabled={pendingIds.length === 0}
                  >
                    Deselect all
                  </Button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 flex flex-col min-h-0 px-4 py-3 gap-3">
              <div className={`${TABLE_SCROLL_CONTAINER_CLASS} flex-1`}>
                <Table>
                  <TableHeader className="sticky top-0 z-10">
                    <TableRow className={TABLE_HEADER_ROW_CLASS}>
                      {/* Checkbox col */}
                      <TableHead className="w-[44px]" style={TABLE_STICKY_HEADER_STYLE} />

                      <TableHead
                        className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[220px]`}
                        style={TABLE_STICKY_HEADER_STYLE}
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          Resource Name
                          {renderSortIcon('name')}
                        </div>
                      </TableHead>

                      <TableHead
                        className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[160px]`}
                        style={TABLE_STICKY_HEADER_STYLE}
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          Role
                          {renderSortIcon('role')}
                        </div>
                      </TableHead>

                      <TableHead
                        className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[150px]`}
                        style={TABLE_STICKY_HEADER_STYLE}
                        onClick={() => handleSort('department')}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          Department
                          {renderSortIcon('department')}
                        </div>
                      </TableHead>

                      <TableHead
                        className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[140px]`}
                        style={TABLE_STICKY_HEADER_STYLE}
                        onClick={() => handleSort('location')}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          Location
                          {renderSortIcon('location')}
                        </div>
                      </TableHead>

                      <TableHead
                        className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[120px]`}
                        style={TABLE_STICKY_HEADER_STYLE}
                        onClick={() => handleSort('type')}
                      >
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          Type
                          {renderSortIcon('type')}
                        </div>
                      </TableHead>

                      <TableHead className={`${TABLE_HEAD_CLASS} min-w-[220px]`} style={TABLE_STICKY_HEADER_STYLE}>
                        <div className="flex items-center gap-1 text-xs font-semibold">
                          <Star className="h-3 w-3 text-muted-foreground" />
                          Skills
                        </div>
                      </TableHead>

                      {calculateUtilization && (
                        <TableHead
                          className={`${TABLE_HEAD_CLASS} cursor-pointer select-none min-w-[160px]`}
                          style={TABLE_STICKY_HEADER_STYLE}
                          onClick={() => handleSort('utilization')}
                        >
                          <div className="flex items-center gap-1 text-xs font-semibold">
                            Utilisation
                            {renderSortIcon('utilization')}
                          </div>
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {displayed.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={calculateUtilization ? 8 : 7}
                          className="py-16 text-center"
                        >
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-5 w-5" />
                            </div>
                            <p className="text-sm font-medium">No resources found</p>
                            <p className="text-xs">
                              Try adjusting your search or filters
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayed.map((resource) => {
                        const isSelected = pendingIds.includes(resource.id);
                        const util = resource.utilization;

                        return (
                          <TableRow
                            key={resource.id}
                            className={cn(
                              TABLE_ROW_CLASS,
                              'cursor-pointer group',
                              isSelected && TABLE_ROW_SELECTED_CLASS
                            )}
                            onClick={() => handleToggle(resource.id)}
                          >
                            {/* Checkbox */}
                            <TableCell
                              className="py-2.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggle(resource.id)}
                                aria-label={`Select ${resource.name}`}
                              />
                            </TableCell>

                            {/* Name + avatar */}
                            <TableCell className="py-2.5">
                              <div className="flex items-center gap-2.5">
                                <div className="relative shrink-0">
                                  <Avatar className="h-8 w-8 rounded-md">
                                    <AvatarImage
                                      src={resource.photoUrl}
                                      alt={resource.name}
                                    />
                                    <AvatarFallback className="rounded-md text-xs font-semibold bg-primary/10 text-primary">
                                      {getInitials(resource.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {isSelected && (
                                    <div className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary flex items-center justify-center">
                                      <CheckCircle2 className="h-2.5 w-2.5 text-primary-foreground" />
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate leading-tight">
                                    {resource.name}
                                  </p>
                                  {resource.email && (
                                    <p className="text-[11px] text-muted-foreground truncate">
                                      {resource.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>

                            {/* Role */}
                            <TableCell className="py-2.5">
                              <div className="flex items-center gap-1.5">
                                <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="text-sm truncate">{resource.role || '—'}</span>
                              </div>
                            </TableCell>

                            {/* Department */}
                            <TableCell className="py-2.5">
                              {resource.department ? (
                                <div className="flex items-center gap-1.5">
                                  <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm truncate">{resource.department}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* Location */}
                            <TableCell className="py-2.5">
                              {resource.location ? (
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm truncate">{resource.location}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* Type */}
                            <TableCell className="py-2.5">
                              <ResourceTypeBadge type={resource.type} />
                            </TableCell>

                            {/* Skills */}
                            <TableCell className="py-2.5">
                              {resource.skills && resource.skills.length > 0 ? (
                                <div className="flex flex-wrap items-center gap-1">
                                  {resource.skills.slice(0, MAX_SKILL_TAGS).map((skill) => (
                                    <SkillTag key={skill.name} skill={skill} />
                                  ))}
                                  {resource.skills.length > MAX_SKILL_TAGS && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground cursor-default leading-none">
                                          +{resource.skills.length - MAX_SKILL_TAGS}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[220px]">
                                        <div className="flex flex-wrap gap-1 py-0.5">
                                          {resource.skills.slice(MAX_SKILL_TAGS).map((skill) => (
                                            <span key={skill.name} className="text-xs">
                                              {skill.name} ({skill.rating}/5)
                                            </span>
                                          ))}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* Utilisation */}
                            {calculateUtilization && (
                              <TableCell className="py-2.5 min-w-[160px]">
                                {util !== undefined ? (
                                  (() => {
                                    const barColor = getTierColor(util, tiers);
                                    return (
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[80px]">
                                          <div
                                            className="h-full rounded-full transition-all"
                                            style={{ width: `${Math.min(util, 100)}%`, backgroundColor: barColor }}
                                          />
                                        </div>
                                        <Badge
                                          className="text-[10px] px-1.5 py-0 h-5 shrink-0 border-transparent"
                                          style={{ backgroundColor: barColor, color: darkenHex(barColor) }}
                                        >
                                          {getTierLabel(util, tiers)}
                                        </Badge>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Circle className="h-3 w-3" />
                                    Unknown
                                  </span>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={sorted.length}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                itemLabel="resources"
                alwaysShow
              />
            </div>
          </div>

          {/* ── Filters sidebar ──────────────────────────────────────────── */}
          {showFilters && (
            <>
              <Separator orientation="vertical" />
              <div className="w-[280px] border-l flex flex-col min-h-0 shrink-0">
                {/* Sidebar header */}
                <div className="px-4 py-2.5 border-b flex items-center justify-between shrink-0">
                  <h3 className="font-semibold text-sm">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowFilters(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Filter fields */}
                <ScrollArea className="flex-1 min-h-0">
                  <div className="px-4 py-4 space-y-5">
                    {/* Available only */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Availability
                      </Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="avail-only"
                          checked={pendingAvailableOnly}
                          onCheckedChange={(c) => setPendingAvailableOnly(c === true)}
                        />
                        <label
                          htmlFor="avail-only"
                          className="text-sm cursor-pointer leading-none"
                        >
                          Show available only
                        </label>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Role
                      </Label>
                      <MultiSelect
                        options={uniqueRoles.map((r) => ({ label: r, value: r }))}
                        value={pendingRoleFilter}
                        onValueChange={setPendingRoleFilter}
                        placeholder="Select roles..."
                      />
                    </div>

                    {/* Department */}
                    {uniqueDepartments.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Department
                        </Label>
                        <MultiSelect
                          options={uniqueDepartments.map((d) => ({ label: d, value: d }))}
                          value={pendingDepartmentFilter}
                          onValueChange={setPendingDepartmentFilter}
                          placeholder="Select departments..."
                        />
                      </div>
                    )}

                    {/* Location */}
                    {uniqueLocations.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Location
                        </Label>
                        <MultiSelect
                          options={uniqueLocations.map((l) => ({ label: l, value: l }))}
                          value={pendingLocationFilter}
                          onValueChange={setPendingLocationFilter}
                          placeholder="Select locations..."
                        />
                      </div>
                    )}

                    {/* Resource type */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Resource Type
                      </Label>
                      <MultiSelect
                        options={RESOURCE_TYPES}
                        value={pendingTypeFilter}
                        onValueChange={setPendingTypeFilter}
                        placeholder="Select types..."
                      />
                    </div>

                    {/* Skills */}
                    {uniqueSkills.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Skills
                        </Label>
                        <MultiSelect
                          options={uniqueSkills.map((s) => ({ label: s, value: s }))}
                          value={pendingSkillFilter}
                          onValueChange={setPendingSkillFilter}
                          placeholder="Filter by skill..."
                        />
                        {pendingSkillFilter.length > 0 && (
                          <p className="text-[10px] text-muted-foreground leading-snug">
                            Shows resources with <strong>all</strong> selected skills
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>

                {/* Sidebar footer actions */}
                <div className="px-4 py-3 border-t space-y-2 shrink-0">
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div className="border-t shrink-0 px-4 py-3 flex items-center justify-between bg-muted/20">
          <p className="text-xs text-muted-foreground">
            {pendingIds.length === 0 ? (
              'No resources selected'
            ) : (
              <>
                <span className="font-medium text-foreground">{pendingIds.length}</span>
                {' '}resource{pendingIds.length !== 1 ? 's' : ''} selected
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirm}
            >
              Confirm selection
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Resource type badge ──────────────────────────────────────────────────────

function ResourceTypeBadge({ type }: { type: ResourceType }) {
  const config: Record<
    ResourceType,
    { label: string; variant: 'outline' | 'secondary' | 'default' }
  > = {
    permanent: { label: 'Permanent', variant: 'outline' },
    contractor: { label: 'Contractor', variant: 'secondary' },
    agency: { label: 'Agency', variant: 'default' },
  };

  const { label, variant } = config[type] ?? { label: type, variant: 'outline' };

  return (
    <Badge variant={variant} className="text-[10px] px-1.5 py-0 h-5 font-medium">
      {label}
    </Badge>
  );
}
